import * as React from 'react';
import GoogleMapReact from 'google-map-react';
import { noop } from 'lodash';
import { Button, IconButton, Typography, withStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { AnimateSharedLayout, AnimatePresence, motion } from 'framer-motion';

import { ENABLE_3D_VIEW, ENABLE_STREET_VIEW } from './index';
import config from '../../../services/config';
import { extractValidUrl, formatShortPrice } from '../../../lib/helpers';
import {
  FavoriteMarker,
  BlueMarker,
  FullScreenIcon,
  ThreeDimensionIcon,
  StreetViewIcon,
} from '../../utility/Icons';
import CustomMarker from '../../utility/GoogleMapContainer/CustomMarker';
import styles from './styles';

declare global {
  interface Window {
    google: any;
  }
}
if (typeof window !== 'undefined') {
  window.google = window.google || {};
}
const HEADING = 90; // Defines the rotation angle around the camera (90 degrees is true east)
const PITCH = 0; // Defines the angle variance "up" or "down" (+90 / -90)
export const STREETVIEW_MAX_DISTANCE = 50; // Street view Distance
interface Props {
  classes: any;
  favorites: any;
  fullScreen?: boolean;
  listing: any;
  router: any;
  on3DPress: Function;
  onMapPress: Function;
  onStreetViewPress: Function;
  closeFullScreen?: Function;
  show3DView?: boolean;
  showStreetView?: boolean;
  listingModalOpen?: boolean;
}

export class GoogleMaps extends React.Component<Props> {
  state = {
    render3DView: false,
    renderStreetView: false,
  };

  componentDidMount() {
    if (
      ENABLE_3D_VIEW &&
      this.props.listing.virtualTourUrl &&
      extractValidUrl(this.props.listing.virtualTourUrl)
    ) {
      this.setState({
        render3DView: true,
      });
    }
  }

  handleGoogleStreeView = () => {
    const { listing } = this.props;
    const google = window.google;
    if (!google && !google.maps) {
      return;
    }
    // Check if this Lat/Lng supports street view
    const streetViewService = new google.maps.StreetViewService();
    const latLng = new google.maps.LatLng(listing.latitude, listing.longitude);
    try {
      streetViewService.getPanoramaByLocation(
        latLng,
        STREETVIEW_MAX_DISTANCE,
        (streetViewPanoramaData: any, status: any) => {
          if (status === google.maps.StreetViewStatus.OK) {
            // Supports street view
            this.setState({ renderStreetView: true });
          } else {
            // No street view available in this range, or some error occurred
            this.setState({ renderStreetView: false });
          }
        },
      );
    } catch (err) {
      // There are no panoramas found that match the search criteria
      this.setState({ renderStreetView: false });
    }
  };

  toggleFullScreen = () => {
    this.props.onMapPress();
  };

  toggle3Dview = (open: boolean) => {
    const { virtualTourUrl } = this.props.listing;
    if (virtualTourUrl.includes('matterport')) {
      this.props.on3DPress(open);
    } else {
      const win: any = window.open(virtualTourUrl, '_blank');
      win.focus();
    }
  };

  toggleStreetView = (open: boolean) => {
    this.props.onStreetViewPress(open);
  };

  closeFullScreen = () => {
    this.props.closeFullScreen && this.props.closeFullScreen();
  };
  generateMarkers = () => {
    const { favorites, listing } = this.props;
    let marker;
    let favorited = false;
    if (favorites && favorites.length > 0) {
      favorited = favorites.find((favorite: any) => {
        return favorite.id === listing.id;
      });
    }
    const price = formatShortPrice(listing.listPrice);
    if (favorited) {
      marker = <FavoriteMarker />;
    } else {
      marker = <BlueMarker content={price} />;
    }

    return (
      <CustomMarker
        isOverlayShown={false}
        key={listing.id}
        lat={listing.latitude}
        listing={listing}
        lng={listing.longitude}
        marker={marker}
        markerClicked={''}
        markerFocused={''}
        onMarkerClick={() => noop}
        onMouseLeaveMarker={() => noop}
        onMouseOverMarker={() => noop}
        overlayListing={() => noop}
        router={this.props.router}
        selectedListing={{}}
      />
    );
  };

  apiIsLoaded = ({ map, maps }: any) => {
    this.handleGoogleStreeView();
  };

  renderMap = () => {
    const { fullScreen, listing } = this.props;
    const center = {
      lat: listing.latitude,
      lng: listing.longitude,
    };
    return (
      <GoogleMapReact
        bootstrapURLKeys={{
          key: config.googleMaps.apiKey,
          libraries: config.googleMaps.libraries,
        }}
        center={center}
        zoom={13}
        onClick={this.toggleFullScreen}
        onTilesLoaded={() => noop}
        onGoogleApiLoaded={this.apiIsLoaded}
        options={{
          clickableIcons: false,
          controlSize: 30,
          disableDoubleClickZoom: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
          keyboardShortcuts: false,
          panControl: false,
          scrollwheel: true,
          zoomControl: fullScreen || false,
        }}
        shouldUnregisterMapOnUnmount
        yesIWantToUseGoogleMapApiInternals
      >
        {this.generateMarkers()}
      </GoogleMapReact>
    );
  };

  handleNearbyClick = () => {
    const { listing } = this.props;
    const win: any = window.open(
      `/search?location=${listing.latitude},${listing.longitude}`,
      '_blank',
    );
    win.focus();
  };

  renderCloseIcon = () => {
    const { classes, closeFullScreen } = this.props;
    return (
      <IconButton
        onClick={() => closeFullScreen && closeFullScreen()}
        className={classes.mapCloseButton}
        size="small"
      >
        <Close htmlColor="black" />
      </IconButton>
    );
  };

  render() {
    const {
      classes,
      fullScreen,
      show3DView,
      showStreetView,
      listing,
    } = this.props;

    if (show3DView) {
      return (
        <React.Fragment>
          <div
            style={{ position: 'absolute', top: 15, zIndex: 5, width: '100%' }}
          >
            {this.renderCloseIcon()}
          </div>
          <div className={classes.matterPortContainer}>
            <iframe
              style={{
                height: '100%',
                width: '100%',
              }}
              src={extractValidUrl(listing.virtualTourUrl)}
            />
          </div>
        </React.Fragment>
      );
    }
    if (showStreetView) {
      // Let the `street-view` div mounts, then pin the map to that div
      setTimeout(() => {
        const { listing } = this.props;
        const panorama = new window.google.maps.StreetViewPanorama(
          document.getElementById('street-view') as HTMLElement,
          {
            position: { lat: listing.latitude, lng: listing.longitude },
            pov: { heading: HEADING, pitch: PITCH },
            zoom: 1,
            fullscreenControl: false,
          },
        );
      }, 1000);
      return (
        <React.Fragment>
          <div
            style={{ position: 'absolute', top: 15, zIndex: 5, width: '100%' }}
          >
            {this.renderCloseIcon()}
          </div>
          <div className={classes.streetViewContainer}>
            <div id="street-view" style={{ height: '100%', width: '100%' }} />
          </div>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        {fullScreen && (
          <div className={classes.nearbyButtonContainer}>
            <Button
              onClick={this.handleNearbyClick}
              className={classes.nearbyButton}
            >
              <Typography className={classes.nearbyText}>
                See Nearby Properties
              </Typography>
            </Button>
            {this.renderCloseIcon()}
          </div>
        )}
        {!fullScreen && (
          <div
            className={classes.fullScreenIcon}
            onClick={this.toggleFullScreen}
          >
            <FullScreenIcon />
          </div>
        )}
        {!fullScreen && (
          <div className={classes.miniMapButtonsContainer}>
            <AnimateSharedLayout>
              <motion.div
                layout
                whileHover={{ scale: 1.2 }}
                initial={false}
                animate={{
                  opacity: this.state.render3DView ? 1 : 0,
                  display: this.state.render3DView ? 'initial' : 'none',
                }}
              >
                <Button
                  className={classes.threeDimensionIcon}
                  classes={{ label: classes.threeDimensionButton }}
                  onClick={() => this.toggle3Dview(true)}
                >
                  <ThreeDimensionIcon />
                  <p className={classes.threeDimensionButtonText}>3D</p>
                </Button>
              </motion.div>
              <AnimatePresence>
                {ENABLE_STREET_VIEW && this.state.renderStreetView && (
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Button
                      className={classes.threeDimensionIcon}
                      classes={{ label: classes.threeDimensionButton }}
                      onClick={() => this.toggleStreetView(true)}
                      style={{
                        margin: this.props.listingModalOpen
                          ? '0px 40px 0px 0px'
                          : '',
                      }}
                    >
                      <StreetViewIcon />
                      <p className={classes.threeDimensionButtonText}>Street</p>
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </AnimateSharedLayout>
          </div>
        )}
        {this.renderMap()}
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(GoogleMaps);
